import Link from "next/link";
import Icon from "/components/common/icon";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { ApiClient } from "/api/client";
import axios from "axios";
import clsx from "clsx";
import Loader from "/components/common/loader";
import { buildSearchHref } from "~/utils/search/searchFilters";
import styles from "./index.module.scss";

/** Сколько позиций показываем в подсказке: остальное — на странице результатов. */
const SUGGEST_LIMIT = 5;

let source;
export default function Search({ isScrolled, activate }) {
  const router = useRouter();
  const [q, setQ] = useState("");
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobileActive, setIsMobileActive] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [isLoadEmpty, setIsLoadEmpty] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (activate) {
      setIsMobileActive(true);
      setIsSelected(true);
    }
  }, [activate]);

  useEffect(() => {
    const body = document.querySelector("body");
    if (isSelected) {
      body.classList.add("popup");
    } else {
      body.classList.remove("popup");
    }
  }, [isSelected]);

  useEffect(() => {
    const getData = setTimeout(async () => {
      setIsLoadEmpty(false);
      setProducts([]);
      setTotal(0);
      if (q.length > 2) {
        source = axios.CancelToken.source();
        setIsLoading(true);
        // format: "full" — вместе с позициями приходит общее число найденного
        // для строки «Показать все результаты (N)» (ТЗ юзабилити, UX-03).
        const response = await ApiClient.catalog.getCatalogSearch(
          q,
          source.token,
          false,
          null,
          { format: "full", suggestLimit: SUGGEST_LIMIT },
        );
        // Пустой массив тоже «ничего не найдено»: раньше здесь была проверка
        // `if (products)`, а [] — truthy, поэтому отбивка никогда не показывалась.
        if (response?.items?.length) {
          setProducts(response.items);
          setTotal(response.total || response.items.length);
        } else {
          setIsLoadEmpty(true);
        }
      } else {
        setProducts([]);
        setTotal(0);
      }
      setIsLoading(false);
    }, 300);
    return () => {
      if (source) {
        source.cancel("test");
      }
      clearTimeout(getData);
    };
  }, [q]);

  const closeSearch = () => {
    setQ("");
    setIsSelected(false);
    setIsMobileActive(false);
  };

  /** Переход на страницу результатов: по Enter и по клику на лупу. */
  const submitSearch = () => {
    const query = q.trim();
    if (!query) {
      inputRef.current?.focus();
      return;
    }
    setIsSelected(false);
    setIsMobileActive(false);
    router.push(buildSearchHref(query));
  };

  const searchHref = buildSearchHref(q.trim());

  return (
    <>
      <div
        className={clsx(
          styles.SearchWrapper,
          isMobileActive && styles.SearchWrapperActive,
        )}
      >
        {isSelected && (
          <div className={styles.Background} onClick={closeSearch} />
        )}
        <div className={clsx(isSelected && styles.SearchActiveBorder)}>
          <div
            className={clsx(styles.Search, isScrolled && styles.SearchScrolled)}
          >
            <input
              data-test="searchInput"
              ref={inputRef}
              placeholder="Поиск по сайту"
              value={q}
              onClick={() => setIsSelected(true)}
              onChange={(event) => setQ(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  submitSearch();
                }
              }}
            />
            <button
              type="button"
              data-test="searchSubmit"
              aria-label="Найти"
              className={styles.Submit}
              onClick={submitSearch}
            >
              <Icon id="search" stroke={"#9294A9"} strokeWidth={2} size={24} />
            </button>
            {isSelected && (
              <div
                className={styles.Close}
                data-test="searchClose"
                onClick={closeSearch}
              >
                <Icon id="close" fill={"#333"} size={24} />
              </div>
            )}
            {(products.length > 0 || isLoading || isLoadEmpty) && (
              <div className={styles.Results} data-test="searchResults">
                {isLoadEmpty && (
                  <div className={styles.LoadEmpty} data-test="searchEmpty">
                    По вашему запросу ничего не найдено :(
                  </div>
                )}
                {isLoading && <Loader />}
                {products.map((item) => (
                  <div
                    className={styles.ItemWrapper}
                    data-test="searchResultItem"
                    key={item.ID}
                  >
                    <Link key={item.ID} href={item.DETAIL_PAGE_URL}>
                      <a className={styles.Item} onClick={closeSearch}>
                        <div className={styles.Photo}>
                          {/* span, а не вложенный <a>: ссылка внутри ссылки —
                              недопустимая вложенность, React ругался в консоли */}
                          <span className={styles.Name}>
                            <img src={item.PHOTO} alt={item.NAME} />
                          </span>
                        </div>
                        <div className={styles.Name}>
                          <div className={styles.Name}>{item.NAME}</div>
                          <div className={styles.Art}>
                            Артикул: {item.ARTICLE.VALUE}
                          </div>
                        </div>
                      </a>
                    </Link>
                    {item.FILE && (
                      <a
                        href={item.FILE}
                        rel="noreferrer"
                        target="_blank"
                        className={styles.File}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <Icon id="pdf" width={16} height={16} />
                        <span>Инструкция по установке</span>
                      </a>
                    )}
                  </div>
                ))}
                {products.length > 0 && (
                  <Link href={searchHref}>
                    <a
                      className={styles.ShowAll}
                      data-test="searchShowAll"
                      onClick={closeSearch}
                    >
                      Показать все результаты ({total})
                    </a>
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
